import { useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { selectChildPageTitle, selectChildPageTitlePiiUserId } from '../../redux/reducers/pageTitleSlice';
import { selectUser } from '../../redux/reducers/userSlice';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';
import {
  ASSIGNMENTS,
  CLASSES_AND_PEOPLE,
  CLASS_REPORTS,
  DATA_EXPORTS,
  HOME,
  PLANNING,
  PageCategory,
  QUICK_DATA,
  SITE_SETTINGS,
  SKILL_CHALLENGES,
} from '../SideNav/pageCategories';

export type { PageCategory };

export interface PageTrail {
  parent: PageCategory | null;
  title: string;
  icon: LucideIcon | null;
  piiUserId: string | null;
}

const EXACT_CATEGORIES: Record<string, PageCategory> = {
  '/': HOME,
  '/classes-rosters': CLASSES_AND_PEOPLE,
  '/students': CLASSES_AND_PEOPLE,
  '/teachers': CLASSES_AND_PEOPLE,
  '/planning': PLANNING,
  '/units': PLANNING,
  '/skills': PLANNING,
  '/assignments': ASSIGNMENTS,
  '/progress': CLASS_REPORTS,
  '/diagnostics': CLASS_REPORTS,
  '/reports-hub': DATA_EXPORTS,
  '/settings': SITE_SETTINGS,
  '/quick-data': QUICK_DATA,
};

const PREFIX_CATEGORIES: [string, PageCategory][] = [
  ['/progress/', CLASS_REPORTS],
  ['/units/', PLANNING],
  ['/skill/', PLANNING],
  ['/assignment/', ASSIGNMENTS],
  ['/students/', CLASSES_AND_PEOPLE],
];

const resolveCategory = (pathname: string): PageCategory | null => {
  if (pathname.startsWith('/assessment-history')) return SKILL_CHALLENGES;

  const prefixMatch = PREFIX_CATEGORIES.find(([prefix]) => pathname.startsWith(prefix));
  if (prefixMatch) return prefixMatch[1];

  return EXACT_CATEGORIES[pathname] ?? null;
};

export const usePageTitle = (): PageTrail => {
  const { pathname } = useLocation();
  const childTitle = useAppSelector(selectChildPageTitle);
  const isAdmin = isUserAdmin(useAppSelector(selectUser));
  const piiUserId = useAppSelector(selectChildPageTitlePiiUserId);

  const category = resolveCategory(pathname);

  if (category === CLASSES_AND_PEOPLE && !isAdmin) {
    const myClasses = { ...CLASSES_AND_PEOPLE, label: 'All classes' };
    return childTitle
      ? { parent: myClasses, title: childTitle, icon: myClasses.icon, piiUserId }
      : { parent: null, title: myClasses.label, icon: myClasses.icon, piiUserId: null };
  }

  if (!category) return { parent: null, title: childTitle ?? '', icon: null, piiUserId };

  if (childTitle) return { parent: category, title: childTitle, icon: category.icon, piiUserId };

  return { parent: null, title: category.label, icon: category.icon, piiUserId: null };
};
